import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { GridFilterConfigProvider } from '@components-library/Grid/GridFilterConfiguration/GridFilterConfigContext';
import { IGridProps } from '@components-library/Grid/interfaces';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import { usePrevious } from '@utils/hooks/usePrevious';
import cn from 'classnames';
import { AppContext } from 'contextStore/context';
import { GridCell } from './GridCell';
import { GridCellsHeader } from './GridCellsHeader';
import { useGridContext } from './GridContext';
import { GridContextSelectionRow } from './GridContextSelectionRow';
import { GridNoDataPlaceholder, GridSpinner } from './GridNoDataPlaceholder';
import { GridPagination } from './GridPagination';
import { GridVisibilityDropdown } from './GridVisibilityDropdown';
import styles from './grid.module.scss';
import { useColumnOrder } from './useColumnOrder';
import { useColumnSizingAndVisibility } from './useColumnSizingAndVisibility';
import { useDefaultSortingSettings } from './useDefaultSortingSettings';

export { createColumnHelper };

export const Grid = <T extends Record<keyof T, unknown>, K extends Record<string, unknown>>({
  data,
  isCountNotAccurate,
  totalCount,
  columns: gridColumns,
  initialState,
  classes,
  rowSelectionEnabled,
  columnResizeEnabled = true,
  headerSection,
  gridName,
  showColumnsConfigurator,
  onPaginationChange,
  getRowId,
  viewMode = 'primary',
  tableLayout = 'fixed',
  processing,
  noDataPlaceholderText,
}: IGridProps<T, K>) => {
  const columns = useDefaultSortingSettings(gridColumns);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const {
    pagination,
    setPagination,
    initialRequest,
    isInitialRequestProcessing,
    setIsInitialRequestProcessing,
    refreshGridData,
    sorting,
    defaultSorting,
    setSorting,
  } = useGridContext();
  const { getValues } = useFormContext<K>();

  const { rowSelection, setRowSelection } = useContext(GridContextSelectionRow);
  const { documentDirection } = useContext(AppContext);

  const { columnVisibility, setColumnVisibility, setColumnSizing, columnSizing } = useColumnSizingAndVisibility({
    gridName,
    defaultVisibilityState: initialState?.columnVisibility,
    defaultSizingState: initialState?.columnSizing,
  });

  const { columnOrder, setColumnOrder } = useColumnOrder({
    gridName,
    columns: gridColumns,
  });

  const previousData = usePrevious(data);

  useEffect(() => {
    if (previousData !== data && Object.keys(rowSelection).length !== 0) {
      setRowSelection({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

      if (newSorting.length === 0) {
        setSorting(defaultSorting);
      } else {
        setSorting(newSorting);
      }
    },
    [defaultSorting, setSorting, sorting],
  );

  useEffect(() => {
    const refresh = async () => {
      if (initialRequest) {
        await refreshGridData();
        setIsInitialRequestProcessing(false);
      }
    };

    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: (updater) => {
      setRowSelection((oldValue) => {
        return typeof updater === 'function' ? updater(oldValue) : updater;
      });
    },
    onColumnVisibilityChange: (updater) => {
      const result = typeof updater === 'function' ? updater(columnVisibility) : updater;

      setColumnVisibility(result);
    },
    onColumnOrderChange: (updater) => {
      const result = typeof updater === 'function' ? updater(columnOrder) : updater;

      setColumnOrder(result);
    },
    initialState,
    pageCount: !totalCount ? -1 : Math.ceil(totalCount / pagination.pageSize),
    state: { pagination, rowSelection, columnVisibility, columnOrder, columnSizing, sorting },
    onSortingChange: handleSortingChange,

    onPaginationChange: (updater) => {
      setPagination((oldValue) => {
        const newValue = typeof updater === 'function' ? updater(oldValue) : updater;

        if (newValue.pageSize !== oldValue.pageSize) {
          newValue.pageIndex = 0;
        }

        void onPaginationChange?.(getValues(), newValue, sorting);
        return newValue;
      });
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;

      setColumnSizing(newSizing);
    },
    manualPagination: true,
    defaultColumn: {
      size: 100,
      minSize: 100,
      maxSize: 500,
    },
    columnResizeMode: 'onChange',
    columnResizeDirection: documentDirection,
    getRowId(originalRow, index, _parent) {
      if (getRowId) {
        return getRowId(originalRow, index, _parent);
      }
      return String((originalRow as Record<string, string>)['id'] ?? index);
    },
  });

  const showNoDataPlaceholder = table.getRowModel().rows.length === 0 && !isInitialRequestProcessing && !processing;

  const hasFooterValues = table
    .getFooterGroups()
    .some(({ headers }) => headers.some((header) => !!header.column.columnDef.footer));

  const { rows } = table.getRowModel();

  const columnsToShowLength = table.getVisibleFlatColumns().length;

  const memoizedRows = useMemo(() => {
    const _neededDependencies = [columnsToShowLength, columnOrder];

    return rows.map((row) => {
      const checked = row?.getIsSelected() ?? table?.getIsAllRowsSelected();
      const disabled = row ? !row.getCanSelect() : false;
      const indeterminate = row?.getIsSomeSelected() ?? table?.getIsSomeRowsSelected();

      return (
        <tr key={row.id} className={cn(styles.row, classes?.row, { [styles.selected]: rowSelection[row.id] })}>
          {row.getVisibleCells().map((cell, index) => {
            return (
              <GridCell
                key={cell.id}
                cell={cell}
                classNameCell={classes?.cell}
                isFirstCell={index === 0}
                row={row}
                rowSelectionEnabled={rowSelectionEnabled}
                selectChecked={checked}
                selectDisabled={disabled}
                selectIndeterminate={indeterminate}
              />
            );
          })}
        </tr>
      );
    });
  }, [classes?.cell, classes?.row, rowSelection, rows, rowSelectionEnabled, table, columnsToShowLength, columnOrder]);

  return (
    <>
      <div
        className={cn(styles.headerSection, { [styles.hidden]: !headerSection && !showColumnsConfigurator })}
        data-view={viewMode}>
        <GridFilterConfigProvider gridName={gridName}>{headerSection}</GridFilterConfigProvider>
        {showColumnsConfigurator && (
          <GridVisibilityDropdown
            className={styles.gridVisibilityDropdown}
            table={table as Table<unknown>}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            setColumnOrder={setColumnOrder}
          />
        )}
      </div>

      <>
        <div ref={tableWrapperRef} className={cn(styles.tableWrapper, classes?.tableWrapper)} data-view={viewMode}>
          <table
            className={cn(styles.table, classes?.table, {
              [styles.tableLayoutAuto]: tableLayout === 'auto',
              [styles.tableLayoutFixed]: tableLayout === 'fixed',
            })}>
            <thead>
              <GridCellsHeader<T>
                table={table}
                tableWrapperRef={tableWrapperRef}
                headerGroups={table.getHeaderGroups()}
                classNameHeaderCell={classes?.headerCell}
                rowSelectionEnabled={rowSelectionEnabled}
                columnResizeEnabled={columnResizeEnabled}
              />
            </thead>

            <tbody className={styles.tbody} data-view={viewMode}>
              <GridSpinner
                columnsLength={table.getVisibleFlatColumns().length}
                show={isInitialRequestProcessing || (viewMode === 'secondary' && Boolean(processing))}
                viewMode={viewMode}
              />
              <GridNoDataPlaceholder
                columnsLength={table.getVisibleFlatColumns().length}
                placeholderText={noDataPlaceholderText}
                show={showNoDataPlaceholder}
                viewMode={viewMode}
              />

              {!processing && memoizedRows}
            </tbody>
            {hasFooterValues && (
              <tfoot className={styles.tfoot}>
                {table.getFooterGroups().map((footerGroup) => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <th key={header.id} className={cn(styles.cell, classes?.cell)}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>

        {!totalCount ? null : (
          <GridPagination
            table={table}
            pagination={pagination}
            totalCount={totalCount}
            isCountNotAccurate={isCountNotAccurate}
          />
        )}
      </>
    </>
  );
};
